import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Download, Star, Copy, MessageSquare, ThumbsUp, BarChart3, Save } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNoIndex } from "@/hooks/useCanonicalTag";

const statusLabels: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  open: { label: "Aberto", variant: "default" },
  resolved: { label: "Resolvido", variant: "secondary" },
  escalated: { label: "Escalado", variant: "destructive" },
};

function StarRating({ rating, max = 5 }: { rating: number; max?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`}
        />
      ))}
    </div>
  );
}

function AverageRatingCard({ conversations }: { conversations: any[] }) {
  const { average, count } = useMemo(() => {
    const rated = conversations.filter((c: any) => c.feedback_rating != null && c.feedback_rating > 0);
    if (rated.length === 0) return { average: 0, count: 0 };
    const sum = rated.reduce((acc: number, c: any) => acc + c.feedback_rating, 0);
    return { average: sum / rated.length, count: rated.length };
  }, [conversations]);

  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }, (_, i) => (
            <Star
              key={i}
              className={`w-6 h-6 ${i < Math.round(average) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`}
            />
          ))}
        </div>
        <div>
          <span className="text-2xl font-bold">{average > 0 ? average.toFixed(1) : "—"}</span>
          <span className="text-sm text-muted-foreground ml-2">/ 5</span>
        </div>
        <span className="text-sm text-muted-foreground">({count} avaliações)</span>
      </CardContent>
    </Card>
  );
}

function ConversationDetailDialog({ conversation, open, onOpenChange }: { conversation: any; open: boolean; onOpenChange: (v: boolean) => void }) {
  const [generatedPrompt, setGeneratedPrompt] = useState<string>("");

  useEffect(() => {
    if (!open) setGeneratedPrompt("");
  }, [open]);

  if (!conversation) return null;

  const userName = (conversation.profiles as any)?.full_name || (conversation.profiles as any)?.email || "Usuário anônimo";

  const generatePrompt = () => {
    const rating = conversation.feedback_rating ? `${conversation.feedback_rating}/5` : "sem nota";
    const feedback = conversation.feedback_text || "Sem texto de feedback";

    const prompt = `O usuário ${userName} enviou o seguinte feedback (nota ${rating}):\n"${feedback}"\n\nPor favor, analise e sugira como implementar/corrigir isso.`;
    setGeneratedPrompt(prompt);
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      toast.success("Prompt copiado para a área de transferência!");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes do Feedback</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Usuário:</span>
              <p className="font-medium">{userName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Data:</span>
              <p className="font-medium">{format(new Date(conversation.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <p><Badge variant={statusLabels[conversation.status]?.variant || "default"}>{statusLabels[conversation.status]?.label || conversation.status}</Badge></p>
            </div>
          </div>

          {conversation.feedback_rating && (
            <div>
              <span className="text-sm text-muted-foreground">Nota:</span>
              <div className="flex items-center gap-2 mt-1">
                <StarRating rating={conversation.feedback_rating} />
                <span className="text-sm font-medium">{conversation.feedback_rating}/5</span>
              </div>
            </div>
          )}

          {conversation.admin_reply && (
            <div>
              <span className="text-sm text-muted-foreground">Resposta enviada {conversation.reply_read_at ? "(lida)" : "(não lida)"}:</span>
              <p className="mt-1 p-3 bg-primary/10 rounded-lg text-sm whitespace-pre-wrap">{conversation.admin_reply}</p>
            </div>
          )}

          {conversation.feedback_text && (
            <div>
              <span className="text-sm text-muted-foreground">Feedback:</span>
              <p className="mt-1 p-3 bg-muted rounded-lg text-sm">{conversation.feedback_text}</p>
            </div>
          )}

          <Button onClick={generatePrompt} variant="outline" className="w-full">
            <Copy className="w-4 h-4 mr-2" />
            Gerar Prompt para Implementação
          </Button>

          {generatedPrompt && (
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Prompt gerado (edite se quiser):</span>
              <Textarea
                value={generatedPrompt}
                onChange={(e) => setGeneratedPrompt(e.target.value)}
                rows={8}
                className="font-mono text-sm"
              />
              <Button onClick={copyPrompt} className="w-full">
                <Copy className="w-4 h-4 mr-2" />
                Copiar prompt
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SupportFeedbacks() {
  useNoIndex();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [replyTarget, setReplyTarget] = useState<any>(null);
  const [replyText, setReplyText] = useState("");
  const openReply = (c: any) => {
    const first = ((c.profiles as any)?.full_name || "").split(" ")[0];
    setReplyText(c.admin_reply || `Oi${first ? " " + first : ""}! Sua solicitação foi atendida. Obrigado pelo contato! — Equipe Data Hire Club`);
    setReplyTarget(c);
  };
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const [localToggle, setLocalToggle] = useState<boolean | null>(null);

  const { data: featureFlag } = useQuery({
    queryKey: ["feature-flag", "support_agent_enabled"],
    queryFn: async () => {
      const { data } = await supabase
        .from("platform_feature_flags")
        .select("*")
        .eq("feature_key", "support_agent_enabled")
        .single();
      return data;
    },
  });

  const hasUnsavedChanges = localToggle !== null && localToggle !== featureFlag?.is_enabled;

  const toggleFlag = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("platform_feature_flags")
        .update({ is_enabled: enabled, updated_by: user?.id, updated_at: new Date().toISOString() })
        .eq("feature_key", "support_agent_enabled");
      if (error) throw error;
    },
    onSuccess: () => {
      setLocalToggle(null);
      queryClient.invalidateQueries({ queryKey: ["feature-flag"] });
      toast.success("Configuração salva com sucesso!");
    },
    onError: (error) => {
      setLocalToggle(null);
      toast.error("Erro ao salvar: " + error.message);
    },
  });

  // Analytics: total usage of the agent by category
  const { data: usageAnalytics } = useQuery({
    queryKey: ["support-usage-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_conversations")
        .select("category");
      if (error) throw error;

      const total = data?.length || 0;
      const byCategory: Record<string, number> = {};
      data?.forEach((row: any) => {
        byCategory[row.category] = (byCategory[row.category] || 0) + 1;
      });

      return { total, byCategory };
    },
  });

  // Only conversations with feedback
  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ["support-feedbacks", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("support_conversations")
        .select("id, created_at, status, feedback_rating, feedback_text, category, user_id, admin_reply, admin_replied_at, reply_read_at, profiles:support_conversations_user_id_profiles_fkey(full_name)")
        .or("feedback_rating.not.is.null,feedback_text.not.is.null")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") query = query.eq("status", statusFilter);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ conversation, status, reply }: { conversation: any; status: string; reply?: string }) => {
      const upd: any = { status };
      if (reply?.trim()) { upd.admin_reply = reply.trim(); upd.admin_replied_at = new Date().toISOString(); upd.reply_read_at = null; }
      const { error: upErr } = await supabase
        .from("support_conversations")
        .update(upd)
        .eq("id", conversation.id);
      if (upErr) throw upErr;

      if (status === "resolved" && conversation.user_id) {
        const excerpt = reply?.trim()
          ? reply.trim().slice(0, 140) + (reply.trim().length > 140 ? "…" : "")
          : "Seu feedback foi analisado e resolvido pela equipe.";
        const { error: notifErr } = await supabase.from("notifications").insert({
          user_id: conversation.user_id,
          type: reply?.trim() ? "support_reply" : "support_resolved",
          title: reply?.trim() ? "💬 O suporte respondeu você" : "✅ Seu feedback foi resolvido",
          message: excerpt,
          link: "/candidate/dashboard?support=open",
        });
        if (notifErr) console.error("Falha ao notificar usuário:", notifErr);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-feedbacks"] });
      setReplyTarget(null);
      toast.success("Status atualizado e usuário notificado!");
    },
    onError: (e: any) => toast.error("Erro: " + e.message),
  });


  const exportCSV = () => {
    const headers = ["Data", "Usuário", "Status", "Nota", "Feedback"];
    const rows = conversations.map((c: any) => [
      format(new Date(c.created_at), "dd/MM/yyyy HH:mm"),
      (c.profiles as any)?.full_name || (c.profiles as any)?.email || "—",
      statusLabels[c.status]?.label || c.status,
      c.feedback_rating || "—",
      c.feedback_text || "—",
    ]);

    const csv = [headers.join(","), ...rows.map(r => r.map((v: string) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `feedbacks-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportado!");
  };

  const categoryLabelsMap: Record<string, string> = {
    feedback: "Feedback",
    functionality: "Funcionalidade",
    navigation: "Navegação",
    human_support: "Suporte Humano",
  };

  return (
    <CandidateLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Suporte & Feedbacks</h1>
            <p className="text-muted-foreground">Gerencie o agente de suporte e visualize feedbacks dos usuários</p>
          </div>
          <Button onClick={exportCSV} variant="outline" disabled={conversations.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>

        {/* Feature flag toggle */}
        <Card className={hasUnsavedChanges ? "ring-2 ring-primary/50" : ""}>
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <h3 className="font-semibold">Agente de Suporte IA</h3>
              <p className="text-sm text-muted-foreground">
                {(localToggle ?? featureFlag?.is_enabled)
                  ? "Visível para todos os usuários"
                  : "Desativado para todos os usuários"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={localToggle ?? featureFlag?.is_enabled ?? false}
                onCheckedChange={v => setLocalToggle(v)}
              />
              {hasUnsavedChanges && (
                <Button
                  size="sm"
                  onClick={() => toggleFlag.mutate(localToggle!)}
                  disabled={toggleFlag.isPending}
                >
                  <Save className="w-4 h-4 mr-1" />
                  Salvar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Analytics de uso */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{usageAnalytics?.total ?? 0}</div>
                <div className="text-xs text-muted-foreground">Total de usos do agente</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <BarChart3 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{usageAnalytics?.byCategory?.["functionality"] ?? 0}</div>
                <div className="text-xs text-muted-foreground">Funcionalidade</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <BarChart3 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{usageAnalytics?.byCategory?.["navigation"] ?? 0}</div>
                <div className="text-xs text-muted-foreground">Navegação</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <ThumbsUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{conversations.length}</div>
                <div className="text-xs text-muted-foreground">Feedbacks recebidos</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Average rating */}
        <AverageRatingCard conversations={conversations} />

        {/* Filter */}
        <div className="flex gap-3 flex-wrap">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-background"
          >
            <option value="all">Todos status</option>
            <option value="open">Aberto</option>
            <option value="resolved">Resolvido</option>
            <option value="escalated">Escalado</option>
          </select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Nota</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Feedback</TableHead>
                  <TableHead>Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : conversations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Nenhum feedback encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  conversations.map((c: any) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedConversation(c)}
                    >
                      <TableCell className="text-sm">
                        {format(new Date(c.created_at), "dd/MM HH:mm", { locale: ptBR })}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(c.profiles as any)?.full_name || "—"}
                      </TableCell>
                      <TableCell>
                        {c.feedback_rating ? <StarRating rating={c.feedback_rating} /> : <span className="text-sm text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusLabels[c.status]?.variant || "default"}>
                          {statusLabels[c.status]?.label || c.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm">
                        {c.feedback_text || "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); openReply(c); }}
                        >
                          {c.status !== "resolved" ? "Resolver" : c.admin_reply ? "Ver resposta" : "Responder"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <ConversationDetailDialog
          conversation={selectedConversation}
          open={!!selectedConversation}
          onOpenChange={(v) => { if (!v) setSelectedConversation(null); }}
        />

        <Dialog open={!!replyTarget} onOpenChange={(v) => { if (!v) setReplyTarget(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Mensagem para o usuário</DialogTitle>
            </DialogHeader>
            {replyTarget && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-3">Pedido: {replyTarget.feedback_text || "—"}</p>
                {replyTarget.admin_replied_at && (
                  <p className="text-xs text-muted-foreground">
                    Respondido em {format(new Date(replyTarget.admin_replied_at), "dd/MM HH:mm", { locale: ptBR })} ·{" "}
                    {replyTarget.reply_read_at ? `Lido em ${format(new Date(replyTarget.reply_read_at), "dd/MM HH:mm", { locale: ptBR })}` : "Ainda não lido"}
                  </p>
                )}
                <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={5} maxLength={1000} />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setReplyTarget(null)}>Cancelar</Button>
                  <Button
                    disabled={!replyText.trim() || updateStatus.isPending}
                    onClick={() => updateStatus.mutate({ conversation: replyTarget, status: "resolved", reply: replyText })}
                  >
                    {updateStatus.isPending ? "Enviando..." : "Enviar e resolver"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </CandidateLayout>
  );
}
